import React from 'react';
import AiSparkIcon from '../common/AiSparkIcon';
import { createPortal } from 'react-dom';
import { useAnnualProgressCrossTopicsForTerm } from '../../hooks/useAnnualProgressCrossTopicsForTerm';
import {
  alignCrossTopicCounts,
  countCrossTopics,
} from '../../utils/annualCrossTopicCounts';
import { readAnnualProgressCrossTopicDetailRows, publishAnnualProgressCrossTopicDetailRows, publishAnnualProgressCrossTopics } from '../../utils/annualProgressCrossTopicRegistry';
import {
  filterAnnualCrossTopicDetailRowsByTopic,
  sortAnnualCrossTopicDetailRows,
  buildAnnualCrossTopicProgramPlanGroups,
  annualCrossTopicProgramPlanTitle,
  type AnnualCrossTopicCountScope,
} from '../../utils/annualProgressCrossTopicExtract';
import AnnualCrossTopicDetailModal from './AnnualCrossTopicDetailModal';
import AnnualCrossTopicProgramPlanModal from './AnnualCrossTopicProgramPlanModal';
import DocumentActionIcon from '../common/DocumentActionIcon';

import type { Grade } from '../../types/domain';
import type { AlignedCrossTopicRow } from '../../utils/annualCrossTopicCounts';
import type { AnnualCrossTopicDetailRow } from '../../utils/annualProgressCrossTopicExtract';
import { gradeHasSyncTimetableProgram } from '../../utils/trackProgramModeStorage';

export type { AnnualCrossTopicCountScope } from '../../utils/annualProgressCrossTopicExtract';

export type AnnualCrossTopicCountModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  grades: Grade[];
  academicYear: number;
  programGradeId: string;
  currentTermNumber: 1 | 2;
  currentTermCrossValues: string[];
  currentTermDetailRows: AnnualCrossTopicDetailRow[];
  schoolScopedCacheSyncReady: boolean;
  /** 마스터 관리자만: 헤더의 'AI 범교과 자동 구성' 버튼 → AI 모달(위에 겹쳐 열림) */
  onOpenAiCompose?: () => void;
  /** 위에 다른 모달이 열려 있으면 Esc 무시 */
  suspendEscape?: boolean;
};

function topicOrderForScope(aligned: AlignedCrossTopicRow[], scope: AnnualCrossTopicCountScope): string[] {
  return aligned
    .filter((row) => {
      if (scope === 'term1') return (row.term1 ?? 0) > 0;
      if (scope === 'term2') return (row.term2 ?? 0) > 0;
      return row.annual > 0;
    })
    .map((row) => row.topic);
}

function filterCrossTopicRowsBySyncTimetable(
  rows: AnnualCrossTopicDetailRow[],
  includeSyncTimetable: boolean
): AnnualCrossTopicDetailRow[] {
  if (includeSyncTimetable) return rows;
  return rows.filter((row) => !row.isSyncTimetableMirror);
}

function CrossTopicPlanButton({
  scope,
  disabled,
  onOpen,
}: {
  scope: AnnualCrossTopicCountScope;
  disabled?: boolean;
  onOpen: (scope: AnnualCrossTopicCountScope) => void;
}) {
  const label = annualCrossTopicProgramPlanTitle(scope);
  return (
    <button
      type="button"
      className="cp-btn-annual-action cp-btn-annual-action-with-document-icon cp-annual-cross-topic-count-plan-btn"
      title={label}
      disabled={disabled}
      onClick={() => onOpen(scope)}
    >
      <DocumentActionIcon />
      <span className="cp-annual-cross-topic-count-plan-btn-label">{label}</span>
    </button>
  );
}

const EMPTY_EXAMPLE_TOPIC = '예시 주제';
const EMPTY_EXAMPLE_COUNT = 3;

function CrossTopicCell({
  topic,
  count,
  onSelect,
  example = false,
}: {
  topic: string;
  count: number | null;
  onSelect: (topic: string) => void;
  /** true면 클릭 불가 예시 블럭(빈 집계일 때 형태만 보여 줌) */
  example?: boolean;
}) {
  if (!example && (count == null || count <= 0)) {
    return <div className="cp-annual-cross-topic-count-slot" aria-hidden />;
  }
  const displayCount = count ?? 0;
  return (
    <div className="cp-annual-cross-topic-count-slot">
      <button
        type="button"
        className={`cp-annual-cross-topic-count-block${example ? ' is-example' : ''}`}
        title={
          example
            ? '예시 — 범교과 주제어가 집계되면 이렇게 표시됩니다'
            : `${topic} ${displayCount}회 — 클릭하여 프로그램 내용 보기`
        }
        disabled={example}
        tabIndex={example ? -1 : undefined}
        aria-disabled={example || undefined}
        onClick={example ? undefined : () => onSelect(topic)}
      >
        <span className="cp-annual-cross-topic-count-block-topic">{topic}</span>
        <span className="cp-annual-cross-topic-count-block-num">{displayCount}</span>
      </button>
    </div>
  );
}

type SyncTimetableToggleProps = {
  included: boolean;
  onChange: (included: boolean) => void;
};

const SyncTimetableToggle: React.FC<SyncTimetableToggleProps> = ({ included, onChange }) => (
  <div
    className="cp-neis-learning-upload-filter-toggle cp-annual-cross-topic-count-sync-toggle"
    aria-label="동기화 시간표 포함 여부"
  >
    <span className="cp-neis-learning-upload-filter-toggle-label">동기화 시간표</span>
    <button
      type="button"
      className={
        'team-auto-approve-toggle cp-neis-learning-upload-exclude-include-toggle' + (included ? ' on' : '')
      }
      onClick={() => onChange(!included)}
      aria-pressed={included}
      aria-label={`동기화 시간표 ${included ? '포함' : '제외'}`}
    >
      <span className="team-auto-approve-toggle-slider" data-on={!included}>
        제외
      </span>
      <span className="team-auto-approve-toggle-slider" data-on={included}>
        포함
      </span>
    </button>
  </div>
);

const AnnualCrossTopicCountModal: React.FC<AnnualCrossTopicCountModalProps> = ({
  open,
  onClose,
  workspaceId,
  grades,
  academicYear,
  programGradeId,
  currentTermNumber,
  currentTermCrossValues: _currentTermCrossValues,
  currentTermDetailRows,
  schoolScopedCacheSyncReady,
  onOpenAiCompose,
  suspendEscape = false,
}) => {
  const [detailPick, setDetailPick] = React.useState<{
    topic: string;
    scope: AnnualCrossTopicCountScope;
  } | null>(null);
  const [planPick, setPlanPick] = React.useState<AnnualCrossTopicCountScope | null>(null);
  /** 기본: 동기화 미러 범교과 제외 */
  const [includeSyncTimetable, setIncludeSyncTimetable] = React.useState(false);

  const showSyncTimetableToggle = React.useMemo(
    () => gradeHasSyncTimetableProgram(workspaceId, programGradeId),
    [programGradeId, workspaceId]
  );

  const effectiveIncludeSyncTimetable = showSyncTimetableToggle && includeSyncTimetable;

  const otherTermNumber: 1 | 2 = currentTermNumber === 1 ? 2 : 1;

  const cachedOtherDetail = React.useMemo(
    () =>
      open
        ? readAnnualProgressCrossTopicDetailRows(workspaceId, academicYear, programGradeId, otherTermNumber)
        : null,
    [academicYear, open, otherTermNumber, programGradeId, workspaceId]
  );

  const needFetchOther = open && !cachedOtherDetail;
  const otherTermFetch = useAnnualProgressCrossTopicsForTerm({
    enabled: needFetchOther,
    workspaceId,
    grades,
    academicYear,
    termNumber: otherTermNumber,
    programGradeId,
    schoolScopedCacheSyncReady,
  });

  React.useEffect(() => {
    if (!open || !needFetchOther || otherTermFetch.loading) return;
    const gid = programGradeId.trim();
    if (!workspaceId.trim() || !gid) return;
    publishAnnualProgressCrossTopicDetailRows(
      workspaceId,
      academicYear,
      gid,
      otherTermNumber,
      otherTermFetch.detailRows
    );
    publishAnnualProgressCrossTopics(
      workspaceId,
      academicYear,
      gid,
      otherTermNumber,
      otherTermFetch.values
    );
  }, [
    academicYear,
    needFetchOther,
    open,
    otherTermFetch.detailRows,
    otherTermFetch.loading,
    otherTermFetch.values,
    otherTermNumber,
    programGradeId,
    workspaceId,
  ]);

  const rawTerm1DetailRows = React.useMemo(() => {
    if (!open) return [];
    if (currentTermNumber === 1) return currentTermDetailRows;
    return cachedOtherDetail ?? otherTermFetch.detailRows;
  }, [cachedOtherDetail, currentTermDetailRows, currentTermNumber, open, otherTermFetch.detailRows]);

  const rawTerm2DetailRows = React.useMemo(() => {
    if (!open) return [];
    if (currentTermNumber === 2) return currentTermDetailRows;
    return cachedOtherDetail ?? otherTermFetch.detailRows;
  }, [cachedOtherDetail, currentTermDetailRows, currentTermNumber, open, otherTermFetch.detailRows]);

  const term1DetailRows = React.useMemo(
    () => filterCrossTopicRowsBySyncTimetable(rawTerm1DetailRows, effectiveIncludeSyncTimetable),
    [effectiveIncludeSyncTimetable, rawTerm1DetailRows]
  );

  const term2DetailRows = React.useMemo(
    () => filterCrossTopicRowsBySyncTimetable(rawTerm2DetailRows, effectiveIncludeSyncTimetable),
    [effectiveIncludeSyncTimetable, rawTerm2DetailRows]
  );

  const term1Values = React.useMemo(
    () => term1DetailRows.map((row) => row.crossTopic),
    [term1DetailRows]
  );

  const term2Values = React.useMemo(
    () => term2DetailRows.map((row) => row.crossTopic),
    [term2DetailRows]
  );

  const aligned = React.useMemo(() => {
    const term1 = countCrossTopics(term1Values);
    const term2 = countCrossTopics(term2Values);
    return alignCrossTopicCounts(term1, term2);
  }, [term1Values, term2Values]);

  const detailModalRows = React.useMemo(() => {
    if (!detailPick) return [];
    if (detailPick.scope === 'term1') {
      return filterAnnualCrossTopicDetailRowsByTopic(term1DetailRows, detailPick.topic);
    }
    if (detailPick.scope === 'term2') {
      return filterAnnualCrossTopicDetailRowsByTopic(term2DetailRows, detailPick.topic);
    }
    return filterAnnualCrossTopicDetailRowsByTopic(
      sortAnnualCrossTopicDetailRows([...term1DetailRows, ...term2DetailRows]),
      detailPick.topic
    );
  }, [detailPick, term1DetailRows, term2DetailRows]);

  const detailModalScopeLabel = detailPick
    ? detailPick.scope === 'term1'
      ? '1학기'
      : detailPick.scope === 'term2'
        ? '2학기'
        : '연간'
    : '';

  const detailModalLoading =
    !!detailPick &&
    ((detailPick.scope === 'term1' && currentTermNumber !== 1 && needFetchOther && otherTermFetch.loading) ||
      (detailPick.scope === 'term2' && currentTermNumber !== 2 && needFetchOther && otherTermFetch.loading) ||
      (detailPick.scope === 'annual' && needFetchOther && otherTermFetch.loading));

  const planModalGroups = React.useMemo(() => {
    if (!planPick) return [];
    const topicOrder = topicOrderForScope(aligned, planPick);
    if (planPick === 'term1') {
      return buildAnnualCrossTopicProgramPlanGroups(term1DetailRows, topicOrder);
    }
    if (planPick === 'term2') {
      return buildAnnualCrossTopicProgramPlanGroups(term2DetailRows, topicOrder);
    }
    return buildAnnualCrossTopicProgramPlanGroups(
      sortAnnualCrossTopicDetailRows([...term1DetailRows, ...term2DetailRows]),
      topicOrder
    );
  }, [aligned, planPick, term1DetailRows, term2DetailRows]);

  const planModalLoading =
    !!planPick &&
    ((planPick === 'term1' && currentTermNumber !== 1 && needFetchOther && otherTermFetch.loading) ||
      (planPick === 'term2' && currentTermNumber !== 2 && needFetchOther && otherTermFetch.loading) ||
      (planPick === 'annual' && needFetchOther && otherTermFetch.loading));

  const otherLoading = needFetchOther && otherTermFetch.loading;

  React.useEffect(() => {
    if (!open) {
      setDetailPick(null);
      setPlanPick(null);
      setIncludeSyncTimetable(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (suspendEscape) return;
        e.preventDefault();
        if (planPick) {
          setPlanPick(null);
          return;
        }
        if (detailPick) {
          setDetailPick(null);
          return;
        }
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [detailPick, onClose, open, planPick, suspendEscape]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="cp-modal-backdrop cp-annual-cross-topic-count-backdrop"
        role="presentation"
        onMouseDown={(ev) => {
          if (ev.target === ev.currentTarget) onClose();
        }}
      >
      <div
        className="cp-modal cp-annual-cross-topic-count-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-annual-cross-topic-count-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <h3
          id="cp-annual-cross-topic-count-title"
          className={onOpenAiCompose ? 'cp-annual-cross-topic-count-title--with-ai' : undefined}
        >
          <span>1·2학기 범교과 주제 카운팅</span>
          {onOpenAiCompose ? (
            <button
              type="button"
              className="cp-ai-feedback-btn cp-annual-cross-topic-count-ai-btn"
              onClick={onOpenAiCompose}
              title="1,2학기 AI 범교과 자동 구성"
            >
              <AiSparkIcon />
              1,2학기 AI 범교과 자동 구성
            </button>
          ) : null}
        </h3>
        {showSyncTimetableToggle ? (
          <div className="cp-annual-cross-topic-count-toolbar">
            <SyncTimetableToggle included={includeSyncTimetable} onChange={setIncludeSyncTimetable} />
            <p className="page-desc cp-annual-cross-topic-count-sync-hint">
              {includeSyncTimetable
                ? '동기화 시간표에 미러링된 범교과도 집계에 포함합니다.'
                : '동기화 시간표에 미러링된 범교과는 집계에서 제외합니다.'}
            </p>
          </div>
        ) : null}
        <div className="cp-modal-body cp-annual-cross-topic-count-body">
          {otherLoading ? (
            <p className="page-desc" role="status" aria-busy="true">
              다른 학기 데이터 불러오는 중…
            </p>
          ) : null}
          {!otherLoading && aligned.length === 0 ? (
            <p className="page-desc cp-annual-cross-topic-count-empty-hint" role="status">
              집계할 범교과 주제어가 없습니다. 아래는 표시 예시예요.
            </p>
          ) : null}
          {!otherLoading ? (
            <div className="cp-annual-cross-topic-count-board">
              {(
                [
                  { scope: 'term1' as const, title: '1학기', countOf: (row: AlignedCrossTopicRow) => row.term1 },
                  { scope: 'term2' as const, title: '2학기', countOf: (row: AlignedCrossTopicRow) => row.term2 },
                  { scope: 'annual' as const, title: '연간', countOf: (row: AlignedCrossTopicRow) => row.annual },
                ] as const
              ).map(({ scope, title, countOf }) => {
                const isEmpty = aligned.length === 0;
                return (
                  <div key={scope} className="cp-annual-cross-topic-count-column">
                    <CrossTopicPlanButton
                      scope={scope}
                      disabled={isEmpty || topicOrderForScope(aligned, scope).length === 0}
                      onOpen={setPlanPick}
                    />
                    <section
                      className={`cp-annual-cross-topic-count-section cp-annual-cross-topic-count-section--${scope}`}
                      aria-label={title}
                    >
                      <h4 className="cp-annual-cross-topic-count-section-title">{title}</h4>
                      <div className="cp-annual-cross-topic-count-section-body">
                        {isEmpty ? (
                          <CrossTopicCell
                            topic={EMPTY_EXAMPLE_TOPIC}
                            count={EMPTY_EXAMPLE_COUNT}
                            example
                            onSelect={() => undefined}
                          />
                        ) : (
                          aligned.map((row) => (
                            <CrossTopicCell
                              key={row.topic}
                              topic={row.topic}
                              count={countOf(row)}
                              onSelect={(topic) => setDetailPick({ topic, scope })}
                            />
                          ))
                        )}
                      </div>
                    </section>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
      </div>
      <AnnualCrossTopicDetailModal
        open={detailPick != null}
        onClose={() => setDetailPick(null)}
        topic={detailPick?.topic ?? ''}
        scope={detailPick?.scope ?? 'term1'}
        scopeLabel={detailModalScopeLabel}
        rows={detailModalRows}
        loading={detailModalLoading}
      />
      <AnnualCrossTopicProgramPlanModal
        open={planPick != null}
        onClose={() => setPlanPick(null)}
        scope={planPick ?? 'term1'}
        groups={planModalGroups}
        loading={planModalLoading}
      />
    </>,
    document.body
  );
};

export default AnnualCrossTopicCountModal;
